import { Fields, ENTITY, EXTERNAL_ASSOCIATION } from '@engoo/view-model';
import vmClient from 'app/services/vmClient';
import { Map, List, Set } from 'immutable';
import {
  CREATE_ENTITY,
  ADD_LIST_ELEMENT,
  REMOVE_LIST_ELEMENT,
  SET_ATTRIBUTE,
  KIND,
} from 'app/models/entities/constants';
import { isCollection, notNull } from 'app/utils/schema';

const validateEntity = (entity) => {
  const schema = entity.schema;

  Object
    .keys([...schema.attributes, ...schema.associations])
    .forEach((attr) => {
      const value = entity[attr];
      validateNull(entity, attr, value);
    });
};

const validateAttributePresence = (entity, attribute) => {
  if (!entity.has(attribute)) {
    throw new Error(
      `Attempting to set non-existent attribute ${attribute} on a ${entity.schema.typeName}.`);
  }
};

const validateNull = (entity, attribute, value) => {
  // TODO: undefined has a somewhat different meaning than null (ex: user passwords).
  // Is there a better way to differentiate that or does the undefined check belong here?
  if ((value === undefined || value === null) && notNull(entity, attribute)) {
    throw new Error(`${entity.schema.typeName} requires ${attribute} to not be null.`);
  }
};

const validateCollection = (entity, attribute) => {
  if (!isCollection(entity, attribute)) {
    const typeName = entity.schema.typeName;
    throw new Error(
      `Attempting to perform an invalid list action on ${attribute}, in ${typeName}.`);
  }
};

const addListElement = (list, value, position) => {
  const filtered = list.filter(x => x !== value);

  if (typeof position === 'number') {
    return filtered.insert(position, value);
  } else {
    return filtered.push(value);
  }
};

/**
 * Builds an entity reducer from a registry.
 *
 * @param {ViewModelClient} vmCli
 * @returns {function}
 */
export const buildEntityReducer = (vmCli) => {
  const actions = Set([
    CREATE_ENTITY,
    SET_ATTRIBUTE,
    ADD_LIST_ELEMENT,
    REMOVE_LIST_ELEMENT,
  ]);

  return (state = Map(), action) => {
    const { payload, type } = action;

    if (actions.has(type)) {
      const {
        [Fields.TYPE]: typeName,
        [Fields.VERSION]: version,
        [KIND]: kind,
        ...props
      } = payload;
      const { id } = payload;
      const storePrefix = vmCli.getStorePrefixByTypeName(typeName);

      if (!id) {
        throw new Error('Expected id not to be falsy.');
      }

      if (kind === EXTERNAL_ASSOCIATION) {
        if (type === ADD_LIST_ELEMENT) {
          const { value, position } = payload;

          return state.updateIn(
            [storePrefix, id],
            (list) => addListElement(list || List(), value, position),
          );
        }

        if (type === REMOVE_LIST_ELEMENT) {
          const { value } = payload;

          return state.updateIn(
            [storePrefix, id],
            (list) => list && list.filter((x) => x !== value),
          );
        }
      } else if (kind === ENTITY) {
        if (type === CREATE_ENTITY) {
          const params = {
            [Fields.TYPE]: typeName,
            [Fields.VERSION]: version,
            ...props,
          };

          const entity = vmCli.forEntity(params)(params);

          if (state.hasIn([storePrefix, id])) {
            throw new Error(`${id} already exists in ${storePrefix}.`);
          }

          validateEntity(entity);

          return state.setIn([storePrefix, id], entity);
        } else if (state.hasIn([storePrefix, id])) {
          const entity = state.getIn([storePrefix, id]);

          if (entity.schema.typeName !== typeName || entity.schema.version !== version) {
            const base = `(${entity.schema.typeName}, ${entity.schema.version})`;
            const remote = `(${typeName}, ${version})`;
            throw new Error(`Attempting to apply updates from ${base} to ${remote}.`);
          }

          // TODO: For polymorphic types, when calling with the super typeName, ensure that
          // setting the attributes are restricted.
          if (type === SET_ATTRIBUTE) {
            const { attribute, value } = payload;

            validateAttributePresence(entity, attribute);
            validateNull(entity, attribute, value);

            return state.setIn([storePrefix, id, attribute], value);
          }

          if (type === ADD_LIST_ELEMENT) {
            const { attribute, value, position } = payload;

            validateCollection(entity, attribute);

            return state.updateIn(
              [storePrefix, id, attribute],
              (list) => addListElement(list || List(), value, position),
            );
          }

          if (type === REMOVE_LIST_ELEMENT) {
            const { attribute, value } = payload;

            validateCollection(entity, attribute);

            return state.updateIn(
              [storePrefix, id, attribute],
              (list) => list && list.filter((x) => x !== value),
            );
          }

          throw new Error(`${type} has no defined behavior.`);
        }
      } else {
        throw new Error('Performing action on unknown type');
      }
    }

    return state;
  };
};

export const entityReducer = buildEntityReducer(vmClient);
